import {ReactNode, useEffect, useMemo, useState} from 'react';
import {useQueryClient} from 'react-query';
import {useIntl} from 'react-intl';
import {
  Alert,
  Button as BootstrapButton,
  Card,
  Col,
  Form,
  Modal,
  OverlayTrigger,
  Row,
  Tooltip,
} from 'react-bootstrap';
import ActionIcon from '../../../../../Components/ActionIcon';
import Badge from '../../../../../app/common/components/Badge';
import Button from '../../../../../Components/Button';
import Level3Title from '../../../../../app/common/components/title/Level3Title';
import {
  PbxGroupeAppel,
  ReglesRedirectionCondition,
  TypeDistribution,
  UTILISATEUR_NON_ATTRIBUE,
} from '../TrunkSip/mocks';
import {
  useGetPbxGroupes,
  useGetVirtualPbxActivite,
  useGetVirtualPbxNumeros,
} from '../../../Hooks/TelephonieManager';
import {useAutoDismissMessage} from '../../../Hooks/useAutoDismissMessage';
import Loading from '../../../../../app/common/components/Loading';

const TYPE_DISTRIBUTION_OPTIONS: {value: TypeDistribution; labelId: string}[] = [
  {value: 'cyclique', labelId: 'TELEPHONIE.PBX.GROUPES.DISTRIBUTION.CYCLIQUE'},
  {value: 'hierarchique', labelId: 'TELEPHONIE.PBX.GROUPES.DISTRIBUTION.HIERARCHIQUE'},
  {value: 'simultane', labelId: 'TELEPHONIE.PBX.GROUPES.DISTRIBUTION.SIMULTANE'},
  {value: 'moins_actif', labelId: 'TELEPHONIE.PBX.GROUPES.DISTRIBUTION.MOINS_ACTIF'},
  {value: 'aleatoire', labelId: 'TELEPHONIE.PBX.GROUPES.DISTRIBUTION.ALEATOIRE'},
];

export const TYPE_DISTRIBUTION_LABEL_ID: Record<TypeDistribution, string> =
  TYPE_DISTRIBUTION_OPTIONS.reduce(
    (acc, option) => ({...acc, [option.value]: option.labelId}),
    {} as Record<TypeDistribution, string>
  );

const GROUPE_SYSTEME_PREFIXES = ['DA ', 'VM ', 'FWD '];

const estGroupeSysteme = (nom: string): boolean =>
  GROUPE_SYSTEME_PREFIXES.some((prefixe) => nom.startsWith(prefixe));

const NB_MAX_APPELANTS_OPTIONS = Array.from({length: 99}, (_, index) => String(index + 1));

const DUREE_SONNERIE_SECONDES = Array.from({length: 11}, (_, index) => (index + 1) * 5);

const DUREE_ATTENTE_MINUTES = [
  ...Array.from({length: 10}, (_, index) => index + 1),
  ...Array.from({length: 10}, (_, index) => 15 + index * 5),
];

const DureeSonnerieOptions = (): JSX.Element => {
  const intl = useIntl();
  return (
    <>
      {DUREE_SONNERIE_SECONDES.map((secondes) => (
        <option key={`s-${secondes}`} value={`${secondes}s`}>
          {secondes} s
        </option>
      ))}
      {DUREE_ATTENTE_MINUTES.map((minutes) => (
        <option key={`m-${minutes}`} value={`${minutes}min`}>
          {minutes} min
        </option>
      ))}
      <option value={DUREE_ATTENTE_ILLIMITE} style={{color: '#f1a53d'}}>
        {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.DUREE.ILLIMITE'})}
      </option>
    </>
  );
};

const DUREE_ATTENTE_ILLIMITE = 'illimite';

export const DEVIER_VERS_OPTIONS: {value: string; labelId: string}[] = [
  {value: 'groupe_ivr_messagerie', labelId: 'TELEPHONIE.PBX.GROUPES.DEVIER.GROUPE_IVR_MESSAGERIE'},
  {value: 'numero', labelId: 'TELEPHONIE.PBX.GROUPES.DEVIER.NUMERO'},
  {value: 'annonce', labelId: 'TELEPHONIE.PBX.GROUPES.DEVIER.ANNONCE'},
  {value: 'fin_appel', labelId: 'TELEPHONIE.PBX.GROUPES.DEVIER.FIN_APPEL'},
];

export const DEVIER_VERS_LABEL_ID: Record<string, string> = DEVIER_VERS_OPTIONS.reduce(
  (acc, option) => ({...acc, [option.value]: option.labelId}),
  {} as Record<string, string>
);

export const NUMEROS_COURTS = Array.from({length: 800}, (_, index) => String(200 + index));

const CONDITION_VIDE: ReglesRedirectionCondition = {message: '', devierVers: 'fin_appel'};

interface GroupeFormState {
  nom: string;
  numeroCourt: string;
  typeDistribution: TypeDistribution;
  dureeSonnerieParAgent: string;
  membres: string[];
  messageAccueil: string;
  messageAttente: string;
  messageAvantReponse: string;
  musiqueAttente: string;
  nbMaxAppelantsFile: string;
  condNbMaxAppelants: ReglesRedirectionCondition;
  dureeAttenteMax: string;
  condDureeAttenteMax: ReglesRedirectionCondition;
  dureeSonnerieMax: string;
  condDureeSonnerieMax: ReglesRedirectionCondition;
  gestionAppelsRepondus: 'inactif' | 'actif';
  desactivationMessage: string;
  desactivationDevierVers: string;
}

const FORM_VIDE: GroupeFormState = {
  nom: '',
  numeroCourt: NUMEROS_COURTS[0],
  typeDistribution: 'cyclique',
  dureeSonnerieParAgent: '15s',
  membres: [],
  messageAccueil: '',
  messageAttente: '',
  messageAvantReponse: '',
  musiqueAttente: '',
  nbMaxAppelantsFile: '5',
  condNbMaxAppelants: CONDITION_VIDE,
  dureeAttenteMax: '1min',
  condDureeAttenteMax: CONDITION_VIDE,
  dureeSonnerieMax: '15s',
  condDureeSonnerieMax: CONDITION_VIDE,
  gestionAppelsRepondus: 'inactif',
  desactivationMessage: '',
  desactivationDevierVers: 'fin_appel',
};

const SectionTitle = ({numero, titleId}: {numero: number; titleId: string}): JSX.Element => {
  const intl = useIntl();
  return (
    <div className="fw-bold text-uppercase fs-6 mb-3 text-decoration-underline">
      {numero}. {intl.formatMessage({id: titleId})}
    </div>
  );
};

const ANNONCES_OPTIONS = [
  'Close',
  'File title',
  'Holiday',
  'New Announcement V2',
  'New DA',
  'New DA',
  'Old Test 2CHF min',
  'Open',
  'Over',
  'Sommer',
  'TestNewWithoutGrp',
];

const SYNTHESE_VOCALE_OPTIONS = ['Cyrano', 'Long2000', 'MessageTTS', 'test log'];

const TONALITES_APPEL_OPTIONS = [
  'Tonalité d’appel par défaut',
  'Open',
  'Standard',
  'Test announcement',
];

const SelectMessage = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (valeur: string) => void;
}): JSX.Element => {
  const intl = useIntl();
  const [enCours, setEnCours] = useState(false);

  const jouerApercu = (): void => {
    try {
      const AudioContextClasse =
        window.AudioContext ||
        (window as unknown as {webkitAudioContext: typeof AudioContext}).webkitAudioContext;
      const contexte = new AudioContextClasse();
      const oscillateur = contexte.createOscillator();
      const gain = contexte.createGain();
      oscillateur.type = 'sine';
      oscillateur.frequency.value = 440;
      oscillateur.connect(gain);
      gain.connect(contexte.destination);
      gain.gain.setValueAtTime(0.1, contexte.currentTime);
      oscillateur.start();
      oscillateur.stop(contexte.currentTime + 0.4);
    } catch {
      // Web Audio API indisponible dans ce contexte — pas d'aperçu sonore possible.
    }
    setEnCours(true);
    window.setTimeout(() => setEnCours(false), 400);
  };

  return (
    <div className="d-flex align-items-center gap-3">
      <Form.Select style={{maxWidth: 320}} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">
          {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.PAS_DE_MESSAGE'})}
        </option>
        <optgroup label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.ANNONCES'})}>
          {ANNONCES_OPTIONS.map((nom, index) => (
            <option key={`${nom}-${index}`} value={nom}>
              {nom}
            </option>
          ))}
        </optgroup>
        <optgroup
          label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.SYNTHESE_VOCALE'})}
        >
          {SYNTHESE_VOCALE_OPTIONS.map((nom, index) => (
            <option key={`${nom}-${index}`} value={nom}>
              {nom}
            </option>
          ))}
        </optgroup>
        <optgroup
          label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.TONALITES_APPEL'})}
        >
          {TONALITES_APPEL_OPTIONS.map((nom, index) => (
            <option key={`${nom}-${index}`} value={nom}>
              {nom}
            </option>
          ))}
        </optgroup>
      </Form.Select>
      {value !== '' && (
        <div className="d-flex align-items-center gap-2">
          <i
            className={`bi ${enCours ? 'bi-pause-fill' : 'bi-play-fill'} fs-4 text-primary cursor-pointer`}
            onClick={jouerApercu}
          />
          <span className="text-muted fs-7">0:00 / 0:01</span>
          <i className="bi bi-dash-lg text-muted" />
          <i className="bi bi-volume-up-fill text-muted" />
        </div>
      )}
    </div>
  );
};

const SelectDevierVers = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (valeur: string) => void;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <Form.Select style={{maxWidth: 320}} value={value} onChange={(e) => onChange(e.target.value)}>
      {DEVIER_VERS_OPTIONS.map((option) => (
        <option key={option.value} value={option.value}>
          {intl.formatMessage({id: option.labelId})}
        </option>
      ))}
    </Form.Select>
  );
};

interface GroupesProps {
  ouvrirAjoutInitial?: boolean;
  onOuvrirAjoutConsomme?: () => void;
  nomGroupeAModifierInitial?: string;
  onOuvrirModificationConsomme?: () => void;
}

const Groupes = ({
  ouvrirAjoutInitial = false,
  onOuvrirAjoutConsomme,
  nomGroupeAModifierInitial,
  onOuvrirModificationConsomme,
}: GroupesProps): JSX.Element | null => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const {data, isLoading} = useGetPbxGroupes();
  const {data: activiteData} = useGetVirtualPbxActivite();
  const {data: numerosData} = useGetVirtualPbxNumeros();
  const [groupesLocal, setGroupesLocal] = useState<PbxGroupeAppel[] | null>(null);
  const [message, setMessage] = useAutoDismissMessage();

  const [vue, setVue] = useState<'liste' | 'formulaire'>('liste');
  const [groupeEnEdition, setGroupeEnEdition] = useState<PbxGroupeAppel | null>(null);
  const [form, setForm] = useState<GroupeFormState>(FORM_VIDE);
  const [groupeASupprimer, setGroupeASupprimer] = useState<PbxGroupeAppel | null>(null);
  const [erreurSuppression, setErreurSuppression] = useState<{nom: string; numero: string} | null>(
    null
  );

  const [statutInlineId, setStatutInlineId] = useState<number | null>(null);
  const [statutInlineValeur, setStatutInlineValeur] = useState(true);

  const [groupeMigration, setGroupeMigration] = useState<PbxGroupeAppel | null>(null);
  const [etapeMigration, setEtapeMigration] = useState<
    'intro' | 'chargement' | 'succes' | 'echec'
  >('intro');

  const [rechercheUtilisateurs, setRechercheUtilisateurs] = useState('');
  const [utilisateursSelectionnes, setUtilisateursSelectionnes] = useState<Set<string>>(new Set());
  const [membresSelectionnes, setMembresSelectionnes] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (data) {
      setGroupesLocal(data.groupes);
    }
  }, [data]);

  const groupes = useMemo(() => groupesLocal ?? data?.groupes ?? [], [groupesLocal, data]);

  const groupesVisibles = useMemo(
    () => groupes.filter((groupe) => !estGroupeSysteme(groupe.nom)),
    [groupes]
  );

  const utilisateursDisponibles = useMemo(
    () =>
      (activiteData?.lignes ?? [])
        .map((ligne) => ligne.telephone)
        .filter((telephone) => !form.membres.includes(telephone))
        .filter((telephone) =>
          rechercheUtilisateurs.trim() === ''
            ? true
            : telephone.toLowerCase().includes(rechercheUtilisateurs.trim().toLowerCase())
        ),
    [activiteData, form.membres, rechercheUtilisateurs]
  );

  const ouvrirAjout = (): void => {
    setGroupeEnEdition(null);
    setForm(FORM_VIDE);
    setUtilisateursSelectionnes(new Set());
    setMembresSelectionnes(new Set());
    setVue('formulaire');
  };

  useEffect(() => {
    if (ouvrirAjoutInitial) {
      ouvrirAjout();
      onOuvrirAjoutConsomme?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvrirAjoutInitial]);

  const ouvrirModification = (groupe: PbxGroupeAppel): void => {
    setGroupeEnEdition(groupe);
    setForm({
      nom: groupe.nom,
      numeroCourt: groupe.numeroCourt,
      typeDistribution: groupe.typeDistribution,
      dureeSonnerieParAgent: groupe.dureeSonnerieParAgent,
      membres: groupe.membres,
      messageAccueil: groupe.messageAccueil,
      messageAttente: groupe.messageAttente,
      messageAvantReponse: groupe.messageAvantReponse,
      musiqueAttente: groupe.musiqueAttente,
      nbMaxAppelantsFile: groupe.nbMaxAppelantsFile,
      condNbMaxAppelants: groupe.condNbMaxAppelants,
      dureeAttenteMax: groupe.dureeAttenteMax,
      condDureeAttenteMax: groupe.condDureeAttenteMax,
      dureeSonnerieMax: groupe.dureeSonnerieMax,
      condDureeSonnerieMax: groupe.condDureeSonnerieMax,
      gestionAppelsRepondus: groupe.gestionAppelsRepondus,
      desactivationMessage: groupe.desactivationMessage,
      desactivationDevierVers: groupe.desactivationDevierVers,
    });
    setUtilisateursSelectionnes(new Set());
    setMembresSelectionnes(new Set());
    setVue('formulaire');
  };

  useEffect(() => {
    if (nomGroupeAModifierInitial) {
      const groupe = groupes.find((candidat) => candidat.nom === nomGroupeAModifierInitial);
      if (groupe) {
        ouvrirModification(groupe);
      }
      onOuvrirModificationConsomme?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nomGroupeAModifierInitial]);

  const basculerSelection = (
    set: Set<string>,
    setSet: (set: Set<string>) => void,
    valeur: string
  ): void => {
    const suivant = new Set(set);
    if (suivant.has(valeur)) {
      suivant.delete(valeur);
    } else {
      suivant.add(valeur);
    }
    setSet(suivant);
  };

  const ajouterMembres = (): void => {
    setForm({...form, membres: [...form.membres, ...Array.from(utilisateursSelectionnes)]});
    setUtilisateursSelectionnes(new Set());
  };

  const retirerMembres = (): void => {
    setForm({
      ...form,
      membres: form.membres.filter((membre) => !membresSelectionnes.has(membre)),
    });
    setMembresSelectionnes(new Set());
  };

  const enregistrer = (): void => {
    const valeurs: Omit<PbxGroupeAppel, 'id'> = {
      nom: form.nom.trim(),
      numeroCourt: form.numeroCourt,
      typeDistribution: form.typeDistribution,
      dureeSonnerieParAgent: form.dureeSonnerieParAgent,
      membres: form.membres,
      messageAccueil: form.messageAccueil,
      messageAttente: form.messageAttente,
      messageAvantReponse: form.messageAvantReponse,
      musiqueAttente: form.musiqueAttente,
      nbMaxAppelantsFile: form.nbMaxAppelantsFile,
      condNbMaxAppelants: form.condNbMaxAppelants,
      dureeAttenteMax: form.dureeAttenteMax,
      condDureeAttenteMax: form.condDureeAttenteMax,
      dureeSonnerieMax: form.dureeSonnerieMax,
      condDureeSonnerieMax: form.condDureeSonnerieMax,
      gestionAppelsRepondus: form.gestionAppelsRepondus,
      desactivationMessage: form.desactivationMessage,
      desactivationDevierVers: form.desactivationDevierVers,
      estAcd: groupeEnEdition?.estAcd ?? true,
      alias: groupeEnEdition?.alias,
      actif: groupeEnEdition?.actif ?? true,
    };
    const groupesMisAJour = groupeEnEdition
      ? groupes.map((groupe) =>
          groupe.id === groupeEnEdition.id ? {id: groupe.id, ...valeurs} : groupe
        )
      : [...groupes, {id: Math.max(0, ...groupes.map((groupe) => groupe.id)) + 1, ...valeurs}];
    setGroupesLocal(groupesMisAJour);
    queryClient.setQueryData('centrex-pbx-groupes', {groupes: groupesMisAJour});
    setVue('liste');
    setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
  };

  const confirmerSuppression = (): void => {
    if (groupeASupprimer) {
      const groupesMisAJour = groupes.filter((groupe) => groupe.id !== groupeASupprimer.id);
      setGroupesLocal(groupesMisAJour);
      queryClient.setQueryData('centrex-pbx-groupes', {groupes: groupesMisAJour});
      setMessage(intl.formatMessage({id: 'TELEPHONIE.SVI.SUCCESS_MESSAGE'}));
    }
    setGroupeASupprimer(null);
  };

  const numeroUtilisantGroupe = (nomGroupe: string) =>
    (numerosData?.numerosGlobaux ?? []).find((numero) =>
      numero.plages.some((plage) => plage.nomGroupe === nomGroupe)
    );

  const demanderSuppression = (groupe: PbxGroupeAppel): void => {
    const numero = numeroUtilisantGroupe(groupe.nom);
    if (numero) {
      setErreurSuppression({nom: groupe.nom, numero: numero.numeroFormate});
      return;
    }
    setErreurSuppression(null);
    setGroupeASupprimer(groupe);
  };

  const mettreAJourGroupe = (id: number, changements: Partial<PbxGroupeAppel>): void => {
    const groupesMisAJour = groupes.map((groupe) =>
      groupe.id === id ? {...groupe, ...changements} : groupe
    );
    setGroupesLocal(groupesMisAJour);
    queryClient.setQueryData('centrex-pbx-groupes', {groupes: groupesMisAJour});
  };

  const ouvrirEditionStatutInline = (groupe: PbxGroupeAppel): void => {
    setStatutInlineId(groupe.id);
    setStatutInlineValeur(groupe.actif);
  };

  const enregistrerStatutInline = (): void => {
    if (statutInlineId != null) {
      mettreAJourGroupe(statutInlineId, {actif: statutInlineValeur});
    }
    setStatutInlineId(null);
  };

  const ouvrirMigration = (groupe: PbxGroupeAppel): void => {
    setGroupeMigration(groupe);
    setEtapeMigration('intro');
  };

  const demarrerMigration = (): void => {
    if (!groupeMigration) {
      return;
    }
    setEtapeMigration('chargement');
    window.setTimeout(() => {
      mettreAJourGroupe(groupeMigration.id, {estAcd: true, actif: true});
      setEtapeMigration('succes');
    }, 1200);
  };

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  if (vue === 'formulaire') {
    return (
      <div>
        <div className="mb-5">
          <h3 className="fw-bold text-uppercase m-0">
            {intl.formatMessage({
              id: groupeEnEdition
                ? 'TELEPHONIE.PBX.GROUPES.FORM.TITLE_EDIT'
                : 'TELEPHONIE.PBX.GROUPES.FORM.TITLE_ADD',
            })}
          </h3>
        </div>

        <Card bg="light" className="mb-5">
          <Card.Body>
            <SectionTitle numero={1} titleId="TELEPHONIE.PBX.GROUPES.SECTION.INFOS" />
            <Row className="g-4">
              <Col xs={12} md={6}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.FORM.NOM'})}{' '}
                  <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={form.nom}
                  onChange={(e) => setForm({...form, nom: e.target.value})}
                />
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.FORM.NUMERO_COURT'})}
                </Form.Label>
                <Form.Select
                  style={{maxWidth: 200}}
                  value={form.numeroCourt}
                  onChange={(e) => setForm({...form, numeroCourt: e.target.value})}
                >
                  {NUMEROS_COURTS.map((numero) => (
                    <option key={numero} value={numero}>
                      {numero}
                    </option>
                  ))}
                </Form.Select>
                <Form.Text className="text-primary fst-italic">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.FORM.NUMERO_COURT_HELP'})}
                </Form.Text>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card bg="light" className="mb-5">
          <Card.Body>
            <SectionTitle numero={2} titleId="TELEPHONIE.PBX.GROUPES.SECTION.DISTRIBUTION" />
            <Row className="g-4">
              <Col xs={12} md={6}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.FORM.TYPE_DISTRIBUTION'})}
                </Form.Label>
                <Form.Select
                  style={{maxWidth: 260}}
                  value={form.typeDistribution}
                  onChange={(e) =>
                    setForm({...form, typeDistribution: e.target.value as TypeDistribution})
                  }
                >
                  {TYPE_DISTRIBUTION_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {intl.formatMessage({id: option.labelId})}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs={12} md={6}>
                <Form.Label className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.FORM.DUREE_SONNERIE_AGENT'})}
                </Form.Label>
                <Form.Select
                  style={{maxWidth: 200}}
                  value={form.dureeSonnerieParAgent}
                  onChange={(e) => setForm({...form, dureeSonnerieParAgent: e.target.value})}
                >
                  <DureeSonnerieOptions />
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card bg="light" className="mb-5">
          <Card.Body>
            <SectionTitle numero={3} titleId="TELEPHONIE.PBX.GROUPES.SECTION.MEMBRES" />
            <p className="fs-6 mb-4">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MEMBRES.INTRO'})}
            </p>
            <Card className="bg-primary text-white mb-4">
              <Card.Body className="d-flex align-items-center gap-4">
                <i className="bi bi-info-circle-fill fs-2x flex-shrink-0" />
                <span className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MEMBRES.INFO'})}
                </span>
              </Card.Body>
            </Card>
            <Form.Label className="fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MEMBRES.RECHERCHER'})}
            </Form.Label>
            <Form.Control
              className="mb-4"
              style={{maxWidth: 320}}
              value={rechercheUtilisateurs}
              onChange={(e) => setRechercheUtilisateurs(e.target.value)}
            />
            <Row className="g-4 align-items-center">
              <Col xs={12} md={5}>
                <div className="fw-bold fs-6 mb-2">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MEMBRES.UTILISATEURS'})}
                </div>
                <div
                  className="border rounded bg-white p-2"
                  style={{height: 220, overflowY: 'auto'}}
                >
                  {utilisateursDisponibles.map((telephone) => (
                    <Form.Check
                      key={telephone}
                      type="checkbox"
                      id={`utilisateur-${telephone}`}
                      label={
                        <span>
                          <i className="bi bi-telephone-fill me-2 text-muted" />
                          {telephone}
                        </span>
                      }
                      checked={utilisateursSelectionnes.has(telephone)}
                      onChange={() =>
                        basculerSelection(
                          utilisateursSelectionnes,
                          setUtilisateursSelectionnes,
                          telephone
                        )
                      }
                    />
                  ))}
                </div>
              </Col>
              <Col xs={12} md={2} className="d-flex flex-column align-items-center gap-3">
                <BootstrapButton
                  variant="primary"
                  size="sm"
                  disabled={utilisateursSelectionnes.size === 0}
                  onClick={ajouterMembres}
                >
                  <i className="bi bi-chevron-double-right" />
                </BootstrapButton>
                <BootstrapButton
                  variant="primary"
                  size="sm"
                  disabled={membresSelectionnes.size === 0}
                  onClick={retirerMembres}
                >
                  <i className="bi bi-chevron-double-left" />
                </BootstrapButton>
              </Col>
              <Col xs={12} md={5}>
                <div className="fw-bold fs-6 mb-2">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MEMBRES.MEMBRES_GROUPE'})}
                </div>
                <div
                  className="border rounded bg-white p-2"
                  style={{height: 220, overflowY: 'auto'}}
                >
                  {form.membres.map((telephone) => (
                    <Form.Check
                      key={telephone}
                      type="checkbox"
                      id={`membre-${telephone}`}
                      label={
                        <span>
                          <i className="bi bi-telephone-fill me-2 text-muted" />
                          {telephone}
                        </span>
                      }
                      checked={membresSelectionnes.has(telephone)}
                      onChange={() =>
                        basculerSelection(membresSelectionnes, setMembresSelectionnes, telephone)
                      }
                    />
                  ))}
                </div>
              </Col>
            </Row>
            <div className="text-muted fs-7 mt-4">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MEMBRES.NOTE'})}
            </div>
          </Card.Body>
        </Card>

        <Card bg="light" className="mb-5">
          <Card.Body>
            <SectionTitle numero={4} titleId="TELEPHONIE.PBX.GROUPES.SECTION.MESSAGES" />
            <Row className="g-5">
              <Col xs={12} md={6}>
                <div className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.ACCUEIL_LABEL'})}
                </div>
                <div className="text-primary fst-italic fs-7 mb-2">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.ACCUEIL_EX'})}
                </div>
                <SelectMessage
                  value={form.messageAccueil}
                  onChange={(valeur) => setForm({...form, messageAccueil: valeur})}
                />
              </Col>
              <Col xs={12} md={6}>
                <div className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.ATTENTE_LABEL'})}
                </div>
                <div className="text-primary fst-italic fs-7 mb-2">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.ATTENTE_EX'})}
                </div>
                <SelectMessage
                  value={form.messageAttente}
                  onChange={(valeur) => setForm({...form, messageAttente: valeur})}
                />
              </Col>
              <Col xs={12} md={6}>
                <div className="fs-6">
                  {intl.formatMessage({
                    id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.AVANT_REPONSE_LABEL',
                  })}
                </div>
                <div className="text-primary fst-italic fs-7 mb-2">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.AVANT_REPONSE_EX'})}
                </div>
                <SelectMessage
                  value={form.messageAvantReponse}
                  onChange={(valeur) => setForm({...form, messageAvantReponse: valeur})}
                />
              </Col>
              <Col xs={12} md={6}>
                <div className="fs-6">
                  {intl.formatMessage({
                    id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.MUSIQUE_ATTENTE_LABEL',
                  })}
                </div>
                <div className="text-primary fst-italic fs-7 mb-2">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MESSAGES.MUSIQUE_ATTENTE_EX'})}
                </div>
                <SelectMessage
                  value={form.musiqueAttente}
                  onChange={(valeur) => setForm({...form, musiqueAttente: valeur})}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card bg="light" className="mb-5">
          <Card.Body>
            <SectionTitle numero={5} titleId="TELEPHONIE.PBX.GROUPES.SECTION.REDIRECTION" />

            <div className="fw-bold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.NB_MAX_APPELANTS'})}
            </div>
            <Form.Select
              className="mb-4"
              style={{maxWidth: 160}}
              value={form.nbMaxAppelantsFile}
              onChange={(e) => setForm({...form, nbMaxAppelantsFile: e.target.value})}
            >
              {NB_MAX_APPELANTS_OPTIONS.map((valeur) => (
                <option key={valeur} value={valeur}>
                  {valeur}
                </option>
              ))}
            </Form.Select>

            <div className="fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.COND_NB_MAX'})}
            </div>
            <div className="text-primary fst-italic fs-7 mb-2">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.COND_NB_MAX_EX'})}
            </div>
            <SelectMessage
              value={form.condNbMaxAppelants.message}
              onChange={(valeur) =>
                setForm({
                  ...form,
                  condNbMaxAppelants: {...form.condNbMaxAppelants, message: valeur},
                })
              }
            />
            <div className="fw-bold fs-6 mt-4">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.DEVIEZ_APPEL'})}
            </div>
            <SelectDevierVers
              value={form.condNbMaxAppelants.devierVers}
              onChange={(valeur) =>
                setForm({
                  ...form,
                  condNbMaxAppelants: {...form.condNbMaxAppelants, devierVers: valeur},
                })
              }
            />

            <Card bg="primary" className="text-white my-5">
              <Card.Body className="d-flex align-items-center gap-4">
                <i className="bi bi-info-circle-fill fs-2x flex-shrink-0" />
                <span className="fs-6">
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.INFO_AUCUN_MEMBRE'})}
                </span>
              </Card.Body>
            </Card>

            <div className="fw-bold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.DUREE_ATTENTE_MAX'})}
            </div>
            <Form.Select
              className="mb-4"
              style={{maxWidth: 160}}
              value={form.dureeAttenteMax}
              onChange={(e) => setForm({...form, dureeAttenteMax: e.target.value})}
            >
              <DureeSonnerieOptions />
            </Form.Select>

            <div className="fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.COND_DUREE_ATTENTE'})}
            </div>
            <div className="text-primary fst-italic fs-7 mb-2">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.COND_NB_MAX_EX'})}
            </div>
            <SelectMessage
              value={form.condDureeAttenteMax.message}
              onChange={(valeur) =>
                setForm({
                  ...form,
                  condDureeAttenteMax: {...form.condDureeAttenteMax, message: valeur},
                })
              }
            />
            <div className="fw-bold fs-6 mt-4">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.DEVIEZ_APPEL'})}
            </div>
            <SelectDevierVers
              value={form.condDureeAttenteMax.devierVers}
              onChange={(valeur) =>
                setForm({
                  ...form,
                  condDureeAttenteMax: {...form.condDureeAttenteMax, devierVers: valeur},
                })
              }
            />

            <hr className="my-5" />

            <div className="fw-bold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.DUREE_SONNERIE_MAX'})}
            </div>
            <Form.Select
              className="mb-4"
              style={{maxWidth: 160}}
              value={form.dureeSonnerieMax}
              onChange={(e) => setForm({...form, dureeSonnerieMax: e.target.value})}
            >
              <DureeSonnerieOptions />
            </Form.Select>

            <div className="fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.COND_DUREE_SONNERIE'})}
            </div>
            <div className="text-primary fst-italic fs-7 mb-2">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.COND_NB_MAX_EX'})}
            </div>
            <SelectMessage
              value={form.condDureeSonnerieMax.message}
              onChange={(valeur) =>
                setForm({
                  ...form,
                  condDureeSonnerieMax: {...form.condDureeSonnerieMax, message: valeur},
                })
              }
            />
            <div className="fw-bold fs-6 mt-4">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.DEVIEZ_APPEL'})}
            </div>
            <SelectDevierVers
              value={form.condDureeSonnerieMax.devierVers}
              onChange={(valeur) =>
                setForm({
                  ...form,
                  condDureeSonnerieMax: {...form.condDureeSonnerieMax, devierVers: valeur},
                })
              }
            />
          </Card.Body>
        </Card>

        <Card bg="light" className="mb-5">
          <Card.Body>
            <SectionTitle numero={6} titleId="TELEPHONIE.PBX.GROUPES.SECTION.APPELS_REPONDUS" />
            <div className="fw-bold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.LABEL'})}
            </div>
            <Form.Select
              className="mb-4"
              style={{maxWidth: 200}}
              value={form.gestionAppelsRepondus}
              onChange={(e) =>
                setForm({
                  ...form,
                  gestionAppelsRepondus: e.target.value as 'inactif' | 'actif',
                })
              }
            >
              <option value="inactif">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.INACTIF'})}
              </option>
              <option value="actif">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.ACTIF'})}
              </option>
            </Form.Select>
            <Card bg="primary" className="text-white">
              <Card.Body className="d-flex align-items-start gap-4">
                <i className="bi bi-info-circle-fill fs-2x flex-shrink-0 mt-1" />
                <span className="fs-6" style={{whiteSpace: 'pre-line'}}>
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.INFO'})}
                </span>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>

        <Card bg="light" className="mb-5">
          <Card.Body>
            <SectionTitle numero={7} titleId="TELEPHONIE.PBX.GROUPES.SECTION.DESACTIVATION" />
            <div className="fw-bold fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.DESACTIVATION.INTRO'})}
            </div>
            <div className="text-primary fst-italic fs-7 mb-4">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.DESACTIVATION.INTRO_DESC'})}
            </div>

            <div className="fs-6">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.DESACTIVATION.MESSAGE_LABEL'})}
            </div>
            <div className="text-primary fst-italic fs-7 mb-2">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.DESACTIVATION.MESSAGE_EX'})}
            </div>
            <SelectMessage
              value={form.desactivationMessage}
              onChange={(valeur) => setForm({...form, desactivationMessage: valeur})}
            />
            <div className="fw-bold fs-6 mt-4 mb-4">
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.REDIRECTION.DEVIEZ_APPEL'})}
            </div>
            <SelectDevierVers
              value={form.desactivationDevierVers}
              onChange={(valeur) => setForm({...form, desactivationDevierVers: valeur})}
            />

            <Card bg="primary" className="text-white mt-5">
              <Card.Body className="d-flex align-items-start gap-4">
                <i className="bi bi-info-circle-fill fs-2x flex-shrink-0 mt-1" />
                <span className="fs-6" style={{whiteSpace: 'pre-line'}}>
                  {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.DESACTIVATION.INFO'})}
                </span>
              </Card.Body>
            </Card>
          </Card.Body>
        </Card>

        <div className="d-flex justify-content-center gap-3">
          <BootstrapButton variant="secondary" onClick={() => setVue('liste')}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <Button
            label={intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
            disabled={form.nom.trim() === ''}
            onClick={enregistrer}
          />
        </div>
      </div>
    );
  }

  return (
    <div>
      <Card bg="light">
        <Card.Body>
          <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.TITLE'})} />
          <p className="text-primary fs-6 mb-5">
            {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.DESCRIPTION'})}
          </p>

          {message && <div className="fw-bold text-danger mb-3">{message}</div>}
          {erreurSuppression && (
            <Alert variant="danger" onClose={() => setErreurSuppression(null)} dismissible>
              {intl.formatMessage(
                {id: 'TELEPHONIE.PBX.GROUPES.AFFECTE_A_UN_NUMERO'},
                {
                  nom: erreurSuppression.nom,
                  numero: erreurSuppression.numero,
                  b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
                }
              )}
            </Alert>
          )}

          <div className="d-flex flex-wrap justify-content-between gap-3">
            <Button
              label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.ADD'})}
              faIcon="bi bi-plus-lg fs-6 me-2"
              onClick={ouvrirAjout}
            />
            <BootstrapButton variant="outline-primary" onClick={() => window.print()}>
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.PRINT_ALL'})}{' '}
              <i className="bi bi-printer ms-2" />
            </BootstrapButton>
          </div>
        </Card.Body>
      </Card>

      {groupesVisibles.length > 0 && (
        <Row className="g-5 mt-5">
          {groupesVisibles.map((groupe) => (
            <Col key={groupe.id} xs={12} xl={6}>
              <Card className="h-100">
                <Card.Header className="d-flex align-items-center justify-content-between flex-wrap gap-3">
                  <div className="d-flex align-items-center gap-3 flex-wrap">
                    <span
                      className="d-flex align-items-center justify-content-center rounded-circle bg-light-primary text-primary flex-shrink-0"
                      style={{width: 36, height: 36}}
                    >
                      <i className="bi bi-people-fill" />
                    </span>
                    <Card.Title className="m-0">
                      <Level3Title title={groupe.nom} />
                    </Card.Title>
                    <Badge text={groupe.numeroCourt} color="primary" light size="small" />
                    {groupe.alias && <Badge text={groupe.alias} color="secondary" light size="small" />}
                    {!groupe.estAcd && (
                      <Badge
                        text={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.OBSOLETE'})}
                        color="warning"
                        light
                        size="small"
                      />
                    )}
                  </div>
                  <div>
                    <ActionIcon
                      label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.EDIT'})}
                      faIcon="fs-3 bi bi-pencil"
                      onClick={() => ouvrirModification(groupe)}
                    />
                    <ActionIcon
                      label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
                      faIcon="fs-3 bi bi-trash text-danger"
                      onClick={() => demanderSuppression(groupe)}
                    />
                  </div>
                </Card.Header>
                <Card.Body>
                  <div className="fw-bold text-uppercase fs-8 text-muted mb-2">
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MEMBRES_TITLE'})}
                  </div>
                  <div className="d-flex flex-wrap gap-2 mb-3">
                    <BootstrapButton
                      size="sm"
                      variant="primary"
                      onClick={() => ouvrirModification(groupe)}
                    >
                      {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.CONFIGURER'})}
                    </BootstrapButton>
                    {!groupe.estAcd && (
                      <OverlayTrigger
                        overlay={
                          <Tooltip>
                            {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MIGRATION.TOOLTIP'})}
                          </Tooltip>
                        }
                      >
                        <BootstrapButton
                          size="sm"
                          variant="outline-primary"
                          onClick={() => ouvrirMigration(groupe)}
                        >
                          <i className="bi bi-arrow-repeat me-2" />
                          {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MIGRATION_ACD'})}
                        </BootstrapButton>
                      </OverlayTrigger>
                    )}
                  </div>

                  {groupe.membres.length === 0 ? (
                    <div className="text-danger fw-semibold fs-7 mb-4">
                      {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.AUCUN_MEMBRE'})}
                    </div>
                  ) : (
                    <div className="mb-4">
                      {groupe.membres.map((telephone) => {
                        const ligne = activiteData?.lignes.find(
                          (candidate) => candidate.telephone === telephone
                        );
                        const estAttribue = (ligne?.utilisateur ?? UTILISATEUR_NON_ATTRIBUE) !== UTILISATEUR_NON_ATTRIBUE;
                        return (
                          <div key={telephone} className="d-flex align-items-center gap-2 mb-1">
                            <span
                              className="rounded-circle flex-shrink-0"
                              style={{
                                width: 8,
                                height: 8,
                                backgroundColor: estAttribue ? '#22c55e' : '#f59e0b',
                              }}
                            />
                            <span className="fs-7">
                              {estAttribue
                                ? ligne?.utilisateur
                                : intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MEMBRE.NON_ASSIGNE'})}
                            </span>
                            <span className="text-muted fs-7">{telephone}</span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="fw-bold text-uppercase fs-8 text-muted mb-2">
                    {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.ROUTAGE_TITLE'})}
                  </div>
                  <div className="fs-7 mb-1">
                    {intl.formatMessage({id: TYPE_DISTRIBUTION_LABEL_ID[groupe.typeDistribution]})}
                  </div>
                  {[
                    {
                      labelId: 'TELEPHONIE.PBX.GROUPES.ROUTAGE.NB_MAX',
                      valeur: groupe.nbMaxAppelantsFile,
                      devierVers: groupe.condNbMaxAppelants.devierVers,
                    },
                    {
                      labelId: 'TELEPHONIE.PBX.GROUPES.ROUTAGE.ATTENTE_MAX',
                      valeur: groupe.dureeAttenteMax,
                      devierVers: groupe.condDureeAttenteMax.devierVers,
                    },
                    {
                      labelId: 'TELEPHONIE.PBX.GROUPES.ROUTAGE.SONNERIE_MAX',
                      valeur: groupe.dureeSonnerieMax,
                      devierVers: groupe.condDureeSonnerieMax.devierVers,
                    },
                  ].map((regle) => (
                    <div key={regle.labelId} className="d-flex align-items-center gap-2 fs-7 text-muted">
                      <span>
                        {intl.formatMessage({id: regle.labelId}, {valeur: regle.valeur})}
                      </span>
                      <i className="bi bi-arrow-right" />
                      <span>
                        {intl.formatMessage({
                          id:
                            DEVIER_VERS_OPTIONS.find((option) => option.value === regle.devierVers)
                              ?.labelId ?? DEVIER_VERS_OPTIONS[DEVIER_VERS_OPTIONS.length - 1].labelId,
                        })}
                      </span>
                    </div>
                  ))}

                  {groupe.estAcd && (
                    <div className={`mt-4 pt-3 border-top ${groupe.actif ? '' : 'opacity-50'}`}>
                      <div className="fw-bold text-uppercase fs-8 text-muted mb-2">
                        {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.STATUT_TITLE'})}
                      </div>
                      {statutInlineId === groupe.id ? (
                        <div className="d-flex align-items-center flex-wrap gap-3">
                          <Form.Check
                            type="radio"
                            id={`statut-actif-${groupe.id}`}
                            name={`statut-${groupe.id}`}
                            label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.ACTIF'})}
                            checked={statutInlineValeur}
                            onChange={() => setStatutInlineValeur(true)}
                          />
                          <Form.Check
                            type="radio"
                            id={`statut-inactif-${groupe.id}`}
                            name={`statut-${groupe.id}`}
                            label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.INACTIF'})}
                            checked={!statutInlineValeur}
                            onChange={() => setStatutInlineValeur(false)}
                          />
                          <BootstrapButton
                            variant="secondary"
                            size="sm"
                            onClick={() => setStatutInlineId(null)}
                          >
                            {intl.formatMessage({id: 'CANCEL'})}
                          </BootstrapButton>
                          <BootstrapButton
                            variant="success"
                            size="sm"
                            onClick={enregistrerStatutInline}
                          >
                            {intl.formatMessage({id: 'TELEPHONIE.SVI.MENU.FORM.SAVE'})}
                          </BootstrapButton>
                        </div>
                      ) : (
                        <div className="d-flex align-items-center gap-3">
                          <Badge
                            text={intl.formatMessage({
                              id: groupe.actif
                                ? 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.ACTIF'
                                : 'TELEPHONIE.PBX.GROUPES.APPELS_REPONDUS.INACTIF',
                            })}
                            color={groupe.actif ? 'success' : 'danger'}
                            light
                            size="small"
                          />
                          <ActionIcon
                            label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.EDIT'})}
                            faIcon="fs-4 bi bi-pencil"
                            onClick={() => ouvrirEditionStatutInline(groupe)}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Modal show={groupeASupprimer != null} onHide={() => setGroupeASupprimer(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage(
            {id: 'TELEPHONIE.PBX.GROUPES.DELETE_BODY'},
            {
              nom: groupeASupprimer?.nom ?? '',
              b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
            }
          )}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setGroupeASupprimer(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerSuppression}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal show={groupeMigration != null} onHide={() => setGroupeMigration(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MIGRATION.TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {etapeMigration === 'intro' && (
            <div>
              <Alert variant="warning" className="d-flex align-items-center gap-3">
                <i className="bi bi-exclamation-triangle-fill fs-3 flex-shrink-0" />
                <span className="fs-7">
                  {intl.formatMessage(
                    {id: 'TELEPHONIE.PBX.GROUPES.MIGRATION.INTRO_BODY'},
                    {
                      nom: groupeMigration?.nom ?? '',
                      b: (chunks: ReactNode[]) => <strong>{chunks}</strong>,
                    }
                  )}
                </span>
              </Alert>
            </div>
          )}
          {etapeMigration === 'chargement' && (
            <div className="d-flex flex-column align-items-center gap-3 py-5">
              <div className="spinner-border text-primary" role="status" />
              <span className="fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MIGRATION.CHARGEMENT'})}
              </span>
            </div>
          )}
          {etapeMigration === 'succes' && (
            <Alert variant="success" className="d-flex align-items-center gap-3 mb-0">
              <i className="bi bi-check-circle-fill fs-3 flex-shrink-0" />
              <span className="fs-6">
                {intl.formatMessage(
                  {id: 'TELEPHONIE.PBX.GROUPES.MIGRATION.SUCCES'},
                  {nom: groupeMigration?.nom ?? ''}
                )}
              </span>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          {etapeMigration === 'intro' && (
            <>
              <BootstrapButton variant="secondary" onClick={() => setGroupeMigration(null)}>
                {intl.formatMessage({id: 'CANCEL'})}
              </BootstrapButton>
              <Button
                label={intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.MIGRATION.DEMARRER'})}
                onClick={demarrerMigration}
              />
            </>
          )}
          {(etapeMigration === 'succes' || etapeMigration === 'echec') && (
            <BootstrapButton variant="secondary" onClick={() => setGroupeMigration(null)}>
              {intl.formatMessage({id: 'TELEPHONIE.PBX.GROUPES.FERMER'})}
            </BootstrapButton>
          )}
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Groupes;
